import { useState, useEffect, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { formatBytes } from "@/components/ResourceFileList";
import AdminResourceForm from "@/components/AdminResourceForm";
import { Upload, Trash2, FileText, Loader2, Plus, Pencil, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const CHUNK = 2 * 1024 * 1024;

function uploadId() {
  return Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("");
}

export default function AdminResourceFiles() {
  const [resources, setResources] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const inputRef = useRef(null);
  const targetRef = useRef(null);

  const load = () => Promise.all([
    api.get("/admin/resources").then((r) => setResources(r.data)),
    api.get("/admin/resource-files").then((r) => setFiles(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const toggleArchive = async (r) => {
    try {
      if (r.active === false) await api.post(`/admin/resources/${r.id}/restore`);
      else await api.delete(`/admin/resources/${r.id}`);
      toast.success(r.active === false ? "Resource restored to the store" : "Resource hidden from the store");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const pick = (resourceId) => { targetRef.current = resourceId; inputRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const resourceId = targetRef.current;
    if (!file || !resourceId) return;
    if (file.size > 50 * 1024 * 1024) { toast.error("File exceeds 50 MB"); return; }
    const id = uploadId();
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    setUploading({ resourceId, pct: 0, name: file.name });
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", id); fd.append("chunk_index", i); fd.append("total_chunks", total);
        fd.append("filename", file.name); fd.append("resource_id", resourceId);
        fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
        await api.post("/admin/uploads/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setUploading({ resourceId, pct: Math.round(((i + 1) / total) * 100), name: file.name });
      }
      toast.success(`${file.name} uploaded`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(null); }
  };

  const remove = async (f) => {
    try {
      await api.delete(`/admin/resources/${f.resource_id}/files/${f.id}`);
      toast.success("File removed");
      load();
    } catch { toast.error("Could not remove file"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-resource-files">
      <input ref={inputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip" onChange={onFile} data-testid="admin-file-input" />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground max-w-2xl">Manage what's on sale and the documents buyers receive. Placeholder editions are marked and can be replaced or removed at any time.</p>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="admin-add-resource-button">
          <Plus className="w-4 h-4 mr-1.5" /> Add resource
        </Button>
      </div>
      {resources.map((r) => {
        const rf = files.filter((f) => f.resource_id === r.id);
        const up = uploading?.resourceId === r.id ? uploading : null;
        const archived = r.active === false;
        return (
          <div key={r.id} className={`bg-card border border-border rounded-2xl p-5 shadow-sm ${archived ? "opacity-70" : ""}`} data-testid={`admin-resource-files-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge variant="secondary">{r.category}</Badge>
                  <span className="text-xs text-muted-foreground" data-testid={`admin-resource-price-${r.id}`}>£{r.price_gbp}</span>
                  {archived && <Badge variant="outline" className="text-[10px]">Hidden from store</Badge>}
                </div>
                <p className="font-display font-semibold">{r.title}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => { setEditing(r); setFormOpen(true); }} data-testid={`admin-edit-resource-${r.id}`}>
                  <Pencil className="w-4 h-4 mr-1.5" /> Edit
                </Button>
                <Button size="sm" variant="ghost" onClick={() => toggleArchive(r)} data-testid={`admin-archive-resource-${r.id}`}>
                  {archived ? <><ArchiveRestore className="w-4 h-4 mr-1.5" /> Restore</> : <><Archive className="w-4 h-4 mr-1.5" /> Hide</>}
                </Button>
                {r.type !== "mock_interview" && (
                  <Button size="sm" onClick={() => pick(r.id)} disabled={!!uploading} data-testid={`admin-upload-button-${r.id}`}>
                    {up ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Upload document
                  </Button>
                )}
              </div>
            </div>
            {up && (
              <div className="mb-3">
                <div className="flex justify-between text-xs text-muted-foreground mb-1"><span className="truncate">{up.name}</span><span>{up.pct}%</span></div>
                <Progress value={up.pct} className="h-1.5" data-testid="admin-upload-progress" />
              </div>
            )}
            {rf.length ? (
              <ul className="divide-y divide-border/60">
                {rf.map((f) => (
                  <li key={f.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`admin-file-row-${f.id}`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-primary shrink-0" />
                      <span className="truncate">{f.original_filename}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{formatBytes(f.size)}</span>
                      {f.is_placeholder && <Badge variant="outline" className="text-[10px] shrink-0">Placeholder</Badge>}
                    </span>
                    <Button size="sm" variant="ghost" className="h-8 text-destructive hover:text-destructive" onClick={() => remove(f)} data-testid={`admin-delete-file-${f.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground italic">{r.type === "mock_interview" ? "AI-led — no documents needed." : "No documents yet — buyers will see \"being prepared\"."}</p>
            )}
          </div>
        );
      })}
      <AdminResourceForm open={formOpen} resource={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />
    </div>
  );
}
